import React from "react";
import { CompanyCanReceivePeppol } from "./CompanyCanReceivePeppol";
import { isNullOrEmptyString } from "utilities";
import { InvoiceV2Api } from "queries/InvoiceV2";
import { CompanyApi } from "queries/Company";
import { Loading } from "components/panels";
import { BookkeepingNotRegisteredForPeppol } from "./BookkeepingNotRegisteredForPeppol";
import { useMutation } from "@tanstack/react-query";
import { ButtonText } from "components/Atoms/Button";
import { PeppolStatus } from "./PeppolStatus";

interface Props {
  invoiceId: number;
  addresseeCompanyId: number;
  invoiceSent: server.dto.InvoiceSent;
}

/**
 * If the invoice is sent with Peppol, we don't need to make any more API calls. We just show that it's sent.
 * If it isn't sent, we check first if the bookkeeping is Peppol ready. If not, we show that
 * If it is Peppol ready, then we check if the recipient can receive Peppol documents
 * If they can't, we show why. If they can, we give the option to send it anyway
 */

export const PeppolAfterInvoiceSent = (props: Props) => {
  const invoiceSidePanelData = InvoiceV2Api.GetSidePanelData.useQuery(props.invoiceId, {
    // Only fetch if invoice hasn't been sent with Peppol
    enabled: props.invoiceSent.receiverPeppolId == null
  });


  const displayErrorIfAny = (result: server.dto.ApiResponse) => {
    if (result.hasErrors) {
      window.displayGenericModal(RESX.ExceptionMessage.ThereIsAProblem, result.errors[0].message, "small", "red");
    } else {
      invoiceSidePanelData.refetch();
    }
  }

  const resendMutation = useMutation({
    mutationFn: (invoiceId: number) => window.CoreApi.Peppol.SubmitInvoice(invoiceId, true).then(r => { displayErrorIfAny(r.data); return r; }),
    onError: () => {
      window.displayGenericModal(RESX.ExceptionMessage.ThereIsAProblem, RESX.GeneralLabels.SomethingUnexpectedHappened, "mini", "red");
    }
  })
  const sendMutation = useMutation({
    mutationFn: (invoiceId: number) => window.CoreApi.Peppol.SubmitInvoice(invoiceId, false).then(r => { displayErrorIfAny(r.data); return r; }),
    onError: () => {
      window.displayGenericModal(RESX.ExceptionMessage.ThereIsAProblem, RESX.GeneralLabels.SomethingUnexpectedHappened, "mini", "red");
    }
  })
  const invoiceSent = invoiceSidePanelData.data?.data.invoiceSent ?? props.invoiceSent;

  const companyCanReceivePeppolApi = CompanyApi.CanReceivePeppol.useQuery(props.addresseeCompanyId, {
    // Only fetch if invoice hasn't been sent with Peppol but bookkeeping is Peppol ready
    enabled: invoiceSent.receiverPeppolId == null && invoiceSidePanelData.data?.data.senderIsPeppolRegistered
  });

  if (!isNullOrEmptyString(invoiceSent.receiverPeppolId)) {
    return <div className="flex gap">
      <div className="grow1">
        <PeppolStatus
          icon="ok"
          label={RESX.EInvoicing.SentOverPeppolNetwork}
          subLabel={invoiceSent.receiverPeppolId}
        />
      </div>
      <div>
        {resendMutation.isLoading && <Loading />}
        {!resendMutation.isLoading && <ButtonText label={RESX.Buttons.SendAgain} onClick={() => resendMutation.mutate(props.invoiceId)} />}
      </div>
    </div>
  }

  if (!invoiceSidePanelData.data) {
    return <Loading />
  }

  if (!invoiceSidePanelData.data.data.senderIsPeppolRegistered) {
    return <BookkeepingNotRegisteredForPeppol />
  }

  if (!companyCanReceivePeppolApi.data) {
    return <Loading />
  }

  if (companyCanReceivePeppolApi.data.data.value !== "foundOnPeppol") {
    return <CompanyCanReceivePeppol compliance={companyCanReceivePeppolApi.data.data} />
  }

  return <div className="flex gap">
    <div className="grow1">
      <PeppolStatus
        icon="warning"
        label={RESX.EInvoicing.NotYetSentOverPeppolNetwork}
        subLabel={companyCanReceivePeppolApi.data.data.messageSublabel}
      />
    </div >
    <div>
      {(sendMutation.isLoading || invoiceSidePanelData.isFetching) && <Loading />}
      {(!sendMutation.isLoading && !invoiceSidePanelData.isFetching) && <ButtonText label={RESX.Buttons.btnSend} onClick={() => sendMutation.mutate(props.invoiceId)} />}
    </div>
  </div >

}